import React, { useState, useEffect, useCallback } from 'react';
import {
  Facebook,
  Settings,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  User,
  Shield,
  FileText,
  MapPin,
  Download,
  Users,
  Calendar,
  Trash2,
  Search
} from 'lucide-react';
import { toast } from 'react-toastify';
import facebookPagesService from '../../../services/integrations/facebookPagesService';
import FacebookFormFieldMapping from './FacebookFormFieldMapping';
import FacebookLeadSync from './FacebookLeadSync';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';

interface FacebookPage {
  id: string;
  name: string;
  category: string;
  picture?: string;
  has_leadgen_access: boolean;
  access_token: string;
}

interface ConnectedPage {
  page_id: string;
  page_name: string;
  connected_at: string;
  facebook_user_name?: string;
  facebook_user_email?: string;
  token_status?: string;
}

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: FacebookFormField[];
}

interface FacebookFormField {
  id: number;
  uuid: string;
  facebook_field_key: string;
  facebook_field_label?: string;
  facebook_field_type: string;
  is_required: boolean;
  field_order: number;
  mapped_crm_column?: string;
  is_active: boolean;
}

const FacebookPagesSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isFetchingPages, setIsFetchingPages] = useState(false);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [connectedPage, setConnectedPage] = useState<ConnectedPage | null>(null);
  const [showPageSelection, setShowPageSelection] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [savingPageId, setSavingPageId] = useState<string | null>(null);

  // Form management state
  const [leadForms, setLeadForms] = useState<FacebookLeadForm[]>([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FacebookLeadForm | null>(null);
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [selectedSyncForm, setSelectedSyncForm] = useState<FacebookLeadForm | null>(null);
  const [showLeadSync, setShowLeadSync] = useState(false);
  const [togglingForm, setTogglingForm] = useState<string | null>(null);

  // Modal states
  const [showFormsModal, setShowFormsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formSearchTerm, setFormSearchTerm] = useState('');

  const features = [
    {
      icon: <Facebook className="w-6 h-6 text-[#1877F2]" />,
      title: "Lead Generation",
      description: "Capture leads directly from Facebook Lead Gen Forms"
    },
    {
      icon: <Users className="w-6 h-6 text-green-600" />,
      title: "Real-time Sync",
      description: "Leads are automatically synced to your CRM in real-time"
    },
    {
      icon: <Shield className="w-6 h-6 text-purple-600" />,
      title: "Secure Connection",
      description: "Encrypted connection with Facebook's Marketing API"
    }
  ];

  useEffect(() => {
    checkConnection();
  }, []);

  // Handle OAuth callback
  const handleOAuthCallback = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const facebookConnected = urlParams.get('facebook_connected');

    if (error) {
      toast.error(`Facebook authentication failed: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (facebookConnected === 'true') {
      toast.success('Facebook connected! Please select a page to connect.');
      setIsAuthenticated(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      loadAvailablePages();
    }
  }, []);

  useEffect(() => {
    handleOAuthCallback();
  }, [handleOAuthCallback]);

  const checkConnection = async () => {
    setIsLoading(true);
    try {
      const authResponse = await facebookPagesService.checkFacebookAuth();
      const pageResponse = await facebookPagesService.getConnectedPage();

      if (authResponse.success && authResponse.data?.is_authenticated) {
        setIsAuthenticated(true);

        if (pageResponse.success && pageResponse.data) {
          setConnectedPage(pageResponse.data);
        } else {
          setConnectedPage(null);
        }
      } else {
        setIsAuthenticated(false);
        setConnectedPage(null);
      }
    } catch (error) {
      console.error('Error checking connection:', error);
      setIsAuthenticated(false);
      setConnectedPage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectFacebook = async () => {
    setIsConnecting(true);
    try {
      const response = await facebookPagesService.generateOAuthUrl();

      if (response.success && response.data) {
        window.location.href = response.data.oauth_url;
      } else {
        toast.error('Failed to generate OAuth URL');
        setIsConnecting(false);
      }
    } catch (error) {
      console.error('Error connecting to Facebook:', error);
      toast.error('Failed to connect to Facebook');
      setIsConnecting(false);
    }
  };

  const loadAvailablePages = async () => {
    setIsFetchingPages(true);
    try {
      const response = await facebookPagesService.getPages();
      if (response.success && response.data) {
        setPages(response.data);
        setShowPageSelection(true);
      } else {
        // If token is invalid or not found, redirect to OAuth
        const errorMsg = response.message || '';
        if (errorMsg.includes('token') || errorMsg.includes('reconnect') || errorMsg.includes('not found')) {
          toast.info('Please reconnect your Facebook account');
          handleConnectFacebook();
        } else {
          toast.error(response.message || 'Failed to load pages');
        }
      }
    } catch (error: any) {
      console.error('Error loading pages:', error);
      // If error suggests token issue, redirect to OAuth
      const errorMsg = error?.message || error?.response?.data?.message || '';
      if (errorMsg.includes('token') || errorMsg.includes('reconnect') || errorMsg.includes('not found')) {
        toast.info('Please reconnect your Facebook account');
        handleConnectFacebook();
      } else {
        toast.error('Failed to load Facebook Pages');
      }
    } finally {
      setIsFetchingPages(false);
    }
  };

  const handleSelectPage = async (page: FacebookPage) => {
    setSavingPageId(page.id);
    try {
      const response = await facebookPagesService.subscribePage(
        page.id,
        page.access_token,
        page.name
      );

      if (response.success) {
        toast.success(`Successfully connected to ${page.name}!`);
        setShowPageSelection(false);
        setPages([]);
        await checkConnection();
      } else {
        toast.error(response.message || 'Failed to connect page');
      }
    } catch (error) {
      console.error('Error subscribing to page:', error);
      toast.error('Failed to connect page');
    } finally {
      setSavingPageId(null);
    }
  };

  const handleDisconnectFacebook = async () => {
    setIsDeleting(true);
    try {
      const response = await facebookPagesService.disconnect();

      if (response.success) {
        toast.success('Facebook Pages integration disconnected');
        setIsAuthenticated(false);
        setConnectedPage(null);
        setPages([]);
        setShowDeleteConfirm(false);
      } else {
        toast.error(response.message || 'Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting Facebook:', error);
      toast.error('Failed to disconnect Facebook integration');
    } finally {
      setIsDeleting(false);
    }
  };

  // Form management functions
  const handleOpenFormsModal = async () => {
    setShowFormsModal(true);
    await loadCachedForms();
  };

  const loadCachedForms = async () => {
    setIsLoadingForms(true);
    try {
      const response = await facebookPagesService.getLeadForms(true);
      if (response.success && response.data) {
        setLeadForms(response.data);
      }
    } catch (error: any) {
      console.error('Error loading cached forms:', error);
    } finally {
      setIsLoadingForms(false);
    }
  };

  const refreshFormsFromFacebook = async () => {
    setIsLoadingForms(true);
    try {
      const response = await facebookPagesService.getLeadForms();
      if (response.success && response.data) {
        setLeadForms(response.data);
        toast.success('Forms refreshed from Facebook');
      } else {
        toast.error(response.message || 'Failed to refresh lead forms');
      }
    } catch (error: any) {
      console.error('Error refreshing lead forms:', error);
      toast.error(error?.message || 'Failed to refresh lead forms');
    } finally {
      setIsLoadingForms(false);
    }
  };

  const handleFormFieldMapping = (form: FacebookLeadForm) => {
    setSelectedForm(form);
    setShowFieldMapping(true);
  };

  const handleSaveMapping = async (formId: string, fieldMapping: any) => {
    try {
      const response = await facebookPagesService.saveFormMapping(formId, fieldMapping);
      if (response.success) {
        toast.success('Field mapping saved successfully!');
        await loadCachedForms();
        setShowFieldMapping(false);
        setSelectedForm(null);
      } else {
        toast.error(response.message || 'Failed to save mapping');
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Failed to save field mapping');
    }
  };

  const handleSyncLeads = (form: FacebookLeadForm) => {
    setSelectedSyncForm(form);
    setShowLeadSync(true);
  };

  const handleLeadSync = async (_formId: string) => {
    await loadCachedForms();
  };

  const handleToggleFormActive = async (formId: string, currentStatus: boolean) => {
    try {
      setTogglingForm(formId);
      const newStatus = !currentStatus;
      await facebookPagesService.toggleFormActive(formId, newStatus);

      toast.success(`Form ${newStatus ? 'activated' : 'deactivated'} successfully`);

      setLeadForms(prevForms =>
        prevForms.map(form =>
          form.form_id === formId ? { ...form, is_active: newStatus } : form
        )
      );
    } catch (error: any) {
      console.error('Error toggling form status:', error);
      toast.error(error.response?.data?.message || 'Failed to update form status');
    } finally {
      setTogglingForm(null);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <ModernSettingHeader
          title="Facebook Pages Integration"
          description="Connect your Facebook pages to receive leads from Lead Gen Forms"
          icon={<Facebook className="h-5 w-5" />}
        />
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-5 w-5 animate-spin text-blue-500 mr-2" />
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Facebook Pages Integration"
        description="Connect your Facebook pages to receive leads from Lead Gen Forms"
        icon={<Facebook className="h-5 w-5" />}
        buttonText={connectedPage ? "Add Another Page" : isAuthenticated ? "Select Facebook Page" : "Connect Facebook Page"}
        onClick={connectedPage || isAuthenticated ? loadAvailablePages : handleConnectFacebook}
      />

      <div className="p-6 space-y-6">
        {/* Integration Benefits */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Integration Benefits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Connected Page */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Connected Page
            </h3>
            <button
              onClick={checkConnection}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {!connectedPage ? (
            <div className="text-center py-8">
              <Facebook className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No Facebook page connected.</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                {isAuthenticated
                  ? 'Facebook is authorized. Click "Select Facebook Page" to choose a page.'
                  : 'Click "Connect Facebook Page" to get started.'}
              </p>
            </div>
          ) : (
            <div className="w-full md:w-1/2">
              <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-[#1877F2]/10 dark:bg-[#1877F2]/20 rounded-xl flex items-center justify-center">
                      <Facebook className="w-6 h-6 text-[#1877F2]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {connectedPage.page_name}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {connectedPage.page_id}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Connected
                  </span>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-600">
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <Calendar className="w-3.5 h-3.5 mr-1" />
                    Connected on {new Date(connectedPage.connected_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                    {connectedPage.facebook_user_name && (
                      <span className="ml-2">by {connectedPage.facebook_user_name}</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleOpenFormsModal}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:hover:bg-purple-900/50 transition-colors"
                    >
                      <Settings className="w-3.5 h-3.5 mr-1.5" />
                      Forms & Mapping
                    </button>
                    <button
                      onClick={isAuthenticated ? loadAvailablePages : handleConnectFacebook}
                      disabled={isConnecting || isFetchingPages}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${(isConnecting || isFetchingPages) ? 'animate-spin' : ''}`} />
                      Reconnect
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Page Selection Modal */}
      <SettingsSlideModal
        isOpen={showPageSelection}
        onClose={() => {
          setShowPageSelection(false);
          setPages([]);
        }}
        title="Select Facebook Page"
        subtitle="Choose a page to connect for lead generation"
        icon={<Facebook className="h-5 w-5" />}
      >
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {pages.length > 0 ? (
            pages.map((page) => (
              <div
                key={page.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center space-x-4">
                  {page.picture ? (
                    <img
                      src={page.picture}
                      alt={page.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{page.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{page.category}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      {page.has_leadgen_access ? (
                        <>
                          <Shield className="h-4 w-4 text-green-500" />
                          <span className="text-xs text-green-600 dark:text-green-400">Lead access available</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          <span className="text-xs text-yellow-600 dark:text-yellow-400">No lead access</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleSelectPage(page)}
                  disabled={!page.has_leadgen_access || savingPageId !== null}
                  className="px-4 py-2 bg-brandColor hover:bg-brand-hover text-white text-sm rounded-lg disabled:opacity-50"
                >
                  {savingPageId === page.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Connect'}
                </button>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Facebook className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No Facebook Pages found</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                Make sure you have pages with lead generation access
              </p>
            </div>
          )}
        </div>
      </SettingsSlideModal>

      {/* Forms Modal */}
      <SettingsSlideModal
        isOpen={showFormsModal}
        onClose={() => {
          setShowFormsModal(false);
          setFormSearchTerm('');
        }}
        title="Lead Generation Forms"
        subtitle={connectedPage?.page_name || 'Configure field mappings and sync leads'}
        icon={<FileText className="h-5 w-5" />}
      >
        <div className="flex flex-col h-full">
          {/* Search and Sync Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Sync your Facebook Lead Gen Forms and configure field mappings.
              </p>
              <button
                onClick={refreshFormsFromFacebook}
                disabled={isLoadingForms}
                className="inline-flex items-center whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg bg-brandColor hover:bg-brand-hover text-white disabled:opacity-50 transition-colors flex-shrink-0 ml-4"
              >
                {isLoadingForms ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Sync Forms
                  </>
                )}
              </button>
            </div>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search forms by name or ID..."
                value={formSearchTerm}
                onChange={(e) => setFormSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              {formSearchTerm && (
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                  {leadForms.filter(f =>
                    f.form_name.toLowerCase().includes(formSearchTerm.toLowerCase()) ||
                    f.form_id.toLowerCase().includes(formSearchTerm.toLowerCase())
                  ).length} results
                </span>
              )}
            </div>
          </div>

          {/* Forms List */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoadingForms ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-brandColor mr-2" />
                <span className="text-gray-600 dark:text-gray-400">Loading forms...</span>
              </div>
            ) : leadForms.length > 0 ? (
              <div className="space-y-3">
                {leadForms
                  .filter(form =>
                    form.form_name.toLowerCase().includes(formSearchTerm.toLowerCase()) ||
                    form.form_id.toLowerCase().includes(formSearchTerm.toLowerCase())
                  )
                  .map((form) => (
                  <div
                    key={form.uuid}
                    className={`p-4 border rounded-lg ${
                      form.is_active
                        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{form.form_name}</h4>
                          {form.field_mapping && Object.keys(form.field_mapping).length > 0 && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                              Mapped
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          ID: {form.form_id}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>Fields: {form.formFields?.length || 0}</span>
                          <span>Synced: {form.total_leads_synced || 0} leads</span>
                          {form.last_sync_at && (
                            <span>Last sync: {new Date(form.last_sync_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Toggle Active */}
                        <button
                          onClick={() => handleToggleFormActive(form.form_id, form.is_active)}
                          disabled={togglingForm === form.form_id}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            form.is_active ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                          title={form.is_active ? 'Click to deactivate' : 'Click to activate'}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              form.is_active ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>

                        {/* Map Fields Button */}
                        <button
                          onClick={() => handleFormFieldMapping(form)}
                          disabled={!form.is_active}
                          className="inline-flex items-center whitespace-nowrap px-3 py-1.5 bg-brandColor hover:bg-brand-hover text-white text-sm rounded-lg disabled:opacity-50"
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          Map
                        </button>

                        {/* Sync Leads Button */}
                        {form.field_mapping && Object.keys(form.field_mapping).length > 0 && (
                          <button
                            onClick={() => handleSyncLeads(form)}
                            disabled={!form.is_active}
                            className="inline-flex items-center whitespace-nowrap px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Sync
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No lead generation forms found</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  Click "Sync Forms" to fetch your Facebook Lead Gen Forms
                </p>
              </div>
            )}
          </div>
        </div>
      </SettingsSlideModal>

      {/* Field Mapping Modal */}
      {selectedForm && (
        <FacebookFormFieldMapping
          form={selectedForm}
          isOpen={showFieldMapping}
          onClose={() => {
            setShowFieldMapping(false);
            setSelectedForm(null);
          }}
          onSave={handleSaveMapping}
        />
      )}

      {/* Lead Sync Modal */}
      {selectedSyncForm && (
        <FacebookLeadSync
          form={selectedSyncForm}
          isOpen={showLeadSync}
          onClose={() => {
            setShowLeadSync(false);
            setSelectedSyncForm(null);
          }}
          onSync={handleLeadSync}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Disconnect Page
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            {connectedPage && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {connectedPage.page_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Page ID: {connectedPage.page_id}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnectFacebook}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Disconnect Page
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacebookPagesSettings;
